<template>
  <div
    v-if="slides.length"
    id="preview-block"
    tabindex="1"
    @keydown.right.prevent="nextElement"
    @keydown.left.prevent="prevElement"
    @keydown.esc.prevent="closePreview">
    <a
      :class="{ 'dsp-none': isOneItem }"
      data-id="left-button"
      class="left carousel-control"
      role="button"
      data-slide="prev">
      <em
        class="qx-icon icon-arrow-left-light left"
        @click="prevElement" />
    </a>
    <v-carousel
      id="carousel-box"
      ref="preview"
      :model-value="currentIndex"
      :show-arrows="false"
      :hide-delimiters="true">
      <v-carousel-item
        v-for="(item, i) in slides"
        :key="item.id">
        <preview-slide
          :file="item"
          :is-share-preview="isSharePreview"
          :current-id="currentId"
          @update:file="(file) => updateFile(i, file)"
          @next="nextElement"
          @prev="prevElement" />
      </v-carousel-item>
    </v-carousel>
    <a
      :class="{ 'dsp-none': isOneItem }"
      data-id="right-button"
      class="right carousel-control"
      role="button"
      data-slide="next"
      @click="nextElement">
      <em class="qx-icon icon-arrow-right-light right" />
    </a>
    <em
      id="close-preview"
      class="qx-icon icon-cancel"
      @click.prevent="closePreview" />
  </div>
</template>

<script>
import { nextTick } from 'vue';
// Vuex
import { mapGetters } from 'vuex';

// Elements
import previewSlide from '@/elements/files/previewSlide.vue';

export default {
  name: 'PreviewFiles',
  components: {
    previewSlide,
  },
  props: {
    collection: {
      type: Array,
      required: true,
    },
    getAllowedActions: {
      type: Function,
      required: true,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    isSharePreview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      list: [],
      slides: [],
      currentId: '',
      index: {
        next: -1,
        current: -1,
        prev: -1,
      },
      isAllSlidesRendered: false,
    };
  },
  computed: {
    ...mapGetters({
      getFileByIdFromStore: 'files/getFileById',
    }),

    isOneItem() {
      return this.list.length === 1;
    },
    currentIndex() {
      return this.slides.findIndex((slide) => slide.id === this.list[this.index.current].id);
    },
  },
  created() {
    const isSelected = this.getFileById(this.selectedIds[0]).isCheckedState;
    const activeEl = this.collection.find((file) => file.isActiveState === true);
    const activeId = activeEl ? activeEl.id : '';
    if (this.selectedIds.length === 1 && this.selectedIds[0] === activeId && !isSelected) {
      this.currentId = activeId;
      this.list = [
        ...this.collection.filter((file) => file.isListed === true && this.getAllowedActions([file.id]).isPreview),
      ];
    } else {
      this.currentId = this.selectedIds[0];
      this.list = [...this.selectedIds.map((fileId) => this.getFileById(fileId))];
    }
  },
  mounted() {
    this.initSlider();
    this.focusPreview();
  },
  methods: {
    updateFile(i, file) {
      this.slides[i] = file;
    },
    initSlider() {
      this.index.current = this.list.findIndex((file) => file.id === this.currentId);
      this.slides = [this.list[this.index.current]];
      this.getElements();
    },
    getFileById(id) {
      const file = this.getFileByIdFromStore(id);
      if (!file) {
        return this.collection.find((item) => item.id === id);
      }
      return file;
    },
    getElements(options = {}) {
      if (options.direction !== 'prev') {
        const nextElement = this.getElement({ direction: 'next' });
        const isInSlides = this.slides.find((file) => file.id === nextElement.id);
        if (!isInSlides) {
          this.slides.push(nextElement);
        }
      }
      if (options.direction !== 'next') {
        const prevElement = this.getElement({ direction: 'prev' });
        const isInSlides = this.slides.find((file) => file.id === prevElement.id);
        if (!isInSlides) {
          this.slides.unshift(prevElement);
        }
      }
      if (this.list.length === this.slides.length) {
        this.isAllSlidesRendered = true;
      }
    },
    getElement(options = {}) {
      let model = this.list[this.index.current];
      if (!model) {
        return undefined;
      }
      const collectionLength = this.list.length;
      let index;
      if (options.direction === 'next') {
        index = this.index.next = this.index.current + 1 === collectionLength ? 0 : this.index.current + 1;
      } else if (options.direction === 'prev') {
        index = this.index.prev = this.index.current === 0 ? collectionLength - 1 : this.index.current - 1;
      }
      return this.list[index];
    },
    nextElement() {
      this.checkBeforeClose();
      this.index.prev = this.index.current;
      this.index.current = this.index.next;
      if (!this.isAllSlidesRendered) {
        this.getElements({ direction: 'next' });
      } else {
        this.getElement({ direction: 'next' });
      }
      this.currentId = this.list[this.index.current].id;
    },
    prevElement() {
      this.checkBeforeClose();
      this.index.next = this.index.current;
      this.index.current = this.index.prev;
      if (!this.isAllSlidesRendered) {
        this.getElements({ direction: 'prev' });
      } else {
        this.getElement({ direction: 'prev' });
      }
      this.currentId = this.list[this.index.current].id;
    },
    checkBeforeClose() {
      const activeItem = this.$el.querySelector('.item.active');
      const activePlayer = activeItem.querySelector('.player');
      activePlayer?.querySelector('video').pause();
      this.focusPreview();
    },
    async focusPreview() {
      await nextTick();
      document.querySelector('#preview-block').focus();
    },
    closePreview() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
#preview-block {
  @extend %dspFlex;
  @include justify-content(space-between);
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  z-index: 1020;
  background: rgba(0, 0, 0, 0.7);
  #carousel-box {
    height: 100% !important;
    width: 70%;
    .item {
      width: 100%;
      height: 100%;
    }
    .v-carousel-item {
      height: 100%;
    }
  }
  .carousel-control {
    @extend %dspFlex;
    @include justify-content(center);
    @include align-items(center);
    z-index: 1030;
    width: 15%;
    color: white;
    opacity: 0.5;
    &:hover {
      text-decoration: none;
      opacity: 0.9;
    }
    .qx-icon {
      font-size: 30px;
    }
  }
}
#close-preview {
  position: absolute;
  z-index: 1040;
  top: 10px;
  right: 10px;
  color: #ffffff;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  opacity: 0.5;
  font-size: 30px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
}
</style>
